import React, { useState, useEffect } from 'react';
import DateRangeSlider from './components/DateRangeSlider';
import { useAppDispatch, useAppSelector } from '../../core/rootHooks';
import { selectDefaultActivityDateRange } from '../main-map/slices/defaultActivityDateRangeSlice';
import {
	selectFeatureDateRangeAnalysisDateRange,
	selectFeatureDateRangeVesselLocationsDate,
	setDateRange,
} from './slices/dateRangeSlice';
import { calculateAnalysisDateRange, calculateVesselLocationDate } from './utils/dateRangeHelpers';
import {
	DATE_RANGE_OPTIONS,
	IAnalysisDateRangeFormData,
	IVesselLocationsDateFormData,
	VESSEL_LOCATION_OPTIONS,
} from './types/dateRangeTypes';
import { parseDate } from '@internationalized/date';
import { extractDateFromISO } from '../../utils/helpers';
import { selectIsUserOnTrial } from '../../user-management/appSelectors';
import { trackExceptionWithContext, trackEventWithContext } from '../../services/application-insights/appInsights';
import {
	AppInsightsComponents,
	AppInsightsErrorTypes,
	AppInsightsActions,
} from '../../services/application-insights/appInsightsConstants';
// import { selectFeature } from '../../routing/pageContextSlice';
import { useLocation } from 'react-router-dom';
import { selectFeature } from '../../routing/pageContextSlice';

interface IDateRangeWithSliderProps {
	/**
	 * Enable dark mode styling
	 */
	darkMode?: boolean;
}

/**
 * Demo component showing how to use the DateRangeSlider component
 */
const DateRangeWithSlider: React.FC<IDateRangeWithSliderProps> = ({ darkMode }) => {
	const dispatch = useAppDispatch();

	const location = useLocation();
	const uriParts = ['/kpis', '/green', '/value', '/dcf', '/download'];

	const mainMapDateRange = useAppSelector(selectDefaultActivityDateRange);
	const vesselLocationsDate = useAppSelector(selectFeatureDateRangeVesselLocationsDate);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);
	const isUserOnTrial = useAppSelector(selectIsUserOnTrial);
	const selectedFeature = useAppSelector(selectFeature);

	const formatDateToYYYYMM = (isoDate: string): number => {
		try {
			const date = new Date(isoDate);
			const year = date.getFullYear();
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			return Number(`${year}${month}`);
		} catch (error) {
			// Only log exceptions for date formatting failures
			trackExceptionWithContext(error instanceof Error ? error : new Error('Date formatting to YYYYMM failed'), {
				component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_PARSING_ERROR,
					isoDate: isoDate,
				},
			});

			// Return current year-month as fallback
			const fallbackDate = new Date();
			const fallbackYear = fallbackDate.getFullYear();
			const fallbackMonth = (fallbackDate.getMonth() + 1).toString().padStart(2, '0');
			return Number(`${fallbackYear}${fallbackMonth}`);
		}
	};

	const [selectedRange, setSelectedRange] = useState<[number, number]>();

	// Update selectedRange when mainMapDateRange changes
	useEffect(() => {
		try {
			if (vesselLocationsDate && vesselLocationsDate.specificDate && analysisDateRange && analysisDateRange.dateRange) {
				const newRange: [number, number] = [
					formatDateToYYYYMM(analysisDateRange.dateRange.start.toString()),
					//formatDateToYYYYMM(mainMapDateRange.endDate.toString()), // use mainMapDateRange for end date
					formatDateToYYYYMM(vesselLocationsDate.specificDate.toString()), // use vesselLocationsDate for end date
				];
				setSelectedRange(newRange);

				// Track successful date range initialization (key user event)
				trackEventWithContext(AppInsightsActions.DATE_RANGE_SELECTION, {
					component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
					additionalData: {
						rangeStart: newRange[0],
						rangeEnd: newRange[1],
						source: 'useEffect-initialization',
					},
				});
			}
		} catch (error) {
			// Only log exceptions for date range initialization failures
			trackExceptionWithContext(error instanceof Error ? error : new Error('Date range initialization failed'), {
				component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
					hasVesselLocationsDate: !!vesselLocationsDate,
					hasSpecificDate: !!vesselLocationsDate?.specificDate,
					hasAnalysisDateRange: !!analysisDateRange,
					hasDateRange: !!analysisDateRange?.dateRange,
				},
			});
		}
	}, [vesselLocationsDate, analysisDateRange]);

	const handleRangeChange = (range: [number, number]) => {
		try {
			setSelectedRange(range);

			// Track the user slider interaction (key user event)
			trackEventWithContext(AppInsightsActions.DATE_RANGE_SLIDER_CHANGE, {
				component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
				additionalData: {
					rangeStart: range[0],
					rangeEnd: range[1],
					source: 'user-slider-interaction',
				},
			});

			// dates should be created as first day of range[0] and last day of range[1]
			// e.g., 202201 -> 2022-01-01, 202212 -> 2022-12-31
			const startDate = `${Math.floor(range[0] / 100)}-${(range[0] % 100).toString().padStart(2, '0')}-01`;
			const endYear = Math.floor(range[1] / 100);
			const endMonth = range[1] % 100;
			const endDate = new Date(endYear, endMonth, 0).getDate();

			// set vessel location date to the end of the selected range
			const vesselLocationsDate: IVesselLocationsDateFormData = {
				selectedOption: VESSEL_LOCATION_OPTIONS.SPECIFIC,
				specificDate: parseDate(`${endYear}-${endMonth.toString().padStart(2, '0')}-${endDate.toString().padStart(2, '0')}`),
			};
			const analysisDateRange: IAnalysisDateRangeFormData = {
				selectedOption: DATE_RANGE_OPTIONS.SPECIFIC_RANGE,
				dateRange: {
					start: parseDate(startDate),
					end: parseDate(`${endYear}-${endMonth.toString().padStart(2, '0')}-${endDate.toString().padStart(2, '0')}`),
				},
				specificYear: null,
				yearMonthRange: {
					start: range[0],
					end: range[1],
				},
			};

			const newDateForVesselLocations = calculateVesselLocationDate(vesselLocationsDate);

			const newDateRangeForAnalysis = calculateAnalysisDateRange(analysisDateRange, {
				start: parseDate(extractDateFromISO(mainMapDateRange.startDate)),
				end: parseDate(extractDateFromISO(mainMapDateRange.endDate)),
			});

			dispatch(
				setDateRange({
					vesselLocationsDate: {
						selectedOption: vesselLocationsDate.selectedOption,
						specificDate: newDateForVesselLocations,
					},
					analysisDateRange: {
						dateRange: newDateRangeForAnalysis,
						selectedOption: analysisDateRange.selectedOption,
						specificYear: analysisDateRange.specificYear,
					},
				}),
			);

			// Track successful date range update (key user event)
			trackEventWithContext(AppInsightsActions.DATE_RANGE_SELECTION, {
				component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
				additionalData: {
					rangeStart: range[0],
					rangeEnd: range[1],
					source: 'user-selection-complete',
				},
			});
		} catch (error) {
			// Only log exceptions for date range change failures
			trackExceptionWithContext(error instanceof Error ? error : new Error('Date range change failed'), {
				component: AppInsightsComponents.DATE_RANGE_WITH_SLIDER,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
					rangeStart: range?.[0],
					rangeEnd: range?.[1],
					mainMapStartDate: mainMapDateRange?.startDate,
					mainMapEndDate: mainMapDateRange?.endDate,
				},
			});
		}
	};

	if (uriParts.includes(location.pathname)) {
		return null; // or return a different component
	}

	if (selectedFeature) {
		return null; // or return a different component
	}

	if (mainMapDateRange && mainMapDateRange.endDate && analysisDateRange && analysisDateRange.dateRange) {
		return (
			<DateRangeSlider
				minDate={isUserOnTrial ? 202401 : 202201} // Set minimum date to Jan 2024 if user is on trial, otherwise Jan 2022
				maxDate={formatDateToYYYYMM(mainMapDateRange.endDate.toString())} // Current year and month in YYYYMM format
				value={selectedRange}
				onChange={handleRangeChange}
				className="absolute bottom-0 z-20 left-36 right-48"
				darkMode={darkMode}
			/>
		);
	} else return null;
};

export default DateRangeWithSlider;
